import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDateTime, highlightText, isImageMedia, linkify, mediaUrl } from '../lib/format';

interface Hit {
  id: number;
  source_id: number;
  platform_message_id: string;
  handle: string;
  platform: string;
  date_utc: string;
  permalink: string | null;
  text: string | null;
  ocr_text: string | null;
  media_path: string | null;
  match_in?: 'text' | 'ocr' | 'both' | 'browse';
}

interface Source {
  id: number;
  handle: string;
}

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const [limit, setLimit] = useState(Number(params.get('limit') ?? 100));
  const [source, setSource] = useState(params.get('source') ?? '');
  const [dateFrom, setDateFrom] = useState(params.get('dateFrom') ?? '');
  const [dateTo, setDateTo] = useState(params.get('dateTo') ?? '');
  const [results, setResults] = useState<Hit[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [browsing, setBrowsing] = useState(false);

  useEffect(() => {
    void api.get<{ sources: Source[] }>('/api/sources').then((d) => setSources(d.sources));
    void api
      .get<{ bookmarks: Array<{ source_id: number; platform_message_id: string }> }>('/api/bookmarks')
      .then((d) => {
        setSaved(new Set(d.bookmarks.map((b) => `${b.source_id}-${b.platform_message_id}`)));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (params.get('browse') === '1' || params.get('source')) {
      void runQuery({ browse: !params.get('q') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, Hit[]>();
    for (const r of results) {
      const list = map.get(r.handle) ?? [];
      list.push(r);
      map.set(r.handle, list);
    }
    return [...map.entries()];
  }, [results]);

  const runQuery = async (opts?: { browse?: boolean; q?: string; source?: string }) => {
    const query = opts?.q !== undefined ? opts.q : q;
    const src = opts?.source !== undefined ? opts.source : source;
    const doBrowse = Boolean(opts?.browse) || !query.trim();
    if (!query.trim() && !doBrowse) return;
    setBusy(true);
    setError(null);
    setSearched(true);
    setBrowsing(!query.trim());
    try {
      const next = new URLSearchParams();
      if (query.trim()) next.set('q', query.trim());
      else next.set('browse', '1');
      next.set('limit', String(limit));
      if (src) next.set('source', src);
      if (dateFrom) next.set('dateFrom', dateFrom);
      if (dateTo) next.set('dateTo', dateTo);
      setParams(next, { replace: true });

      const apiParams = new URLSearchParams({ limit: String(limit) });
      if (query.trim()) apiParams.set('q', query.trim());
      else apiParams.set('browse', '1');
      if (src) apiParams.set('source', src);
      if (dateFrom) apiParams.set('dateFrom', dateFrom);
      if (dateTo) apiParams.set('dateTo', dateTo);
      const data = await api.get<{ results: Hit[] }>(`/api/search?${apiParams}`);
      setResults(data.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Zoeken mislukt');
    } finally {
      setBusy(false);
    }
  };

  const onSearch = async (e?: FormEvent) => {
    e?.preventDefault();
    await runQuery({ browse: !q.trim() });
  };

  const browseAll = async () => {
    setQ('');
    await runQuery({ browse: true, q: '' });
  };

  const toggleSave = async (hit: Hit) => {
    const key = `${hit.source_id}-${hit.platform_message_id}`;
    if (saved.has(key)) {
      await api.delete('/api/bookmarks', {
        sourceId: hit.source_id,
        platformMessageId: hit.platform_message_id,
      });
      setSaved((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    } else {
      await api.post('/api/bookmarks', {
        sourceId: hit.source_id,
        platformMessageId: hit.platform_message_id,
      });
      setSaved((prev) => new Set(prev).add(key));
    }
  };

  const exportUrl = (() => {
    const p = new URLSearchParams({ limit: String(limit) });
    if (q.trim()) p.set('q', q.trim());
    else p.set('browse', '1');
    if (source) p.set('source', source);
    if (dateFrom) p.set('dateFrom', dateFrom);
    if (dateTo) p.set('dateTo', dateTo);
    return `/api/search/export.csv?${p}`;
  })();

  return (
    <div>
      <h1>Berichten</h1>
      <p className="page-lead">
        Zoek in tekst én OCR, of blader door alle ingeladen berichten zonder zoekterm.
      </p>

      <form className="panel toolbar" onSubmit={(e) => void onSearch(e)}>
        <div className="field" style={{ flex: 1, minWidth: 220 }}>
          <label>Zoekterm (optioneel)</label>
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="trefwoorden… (leeg = alles tonen)" autoFocus />
        </div>
        <div className="field">
          <label>Bron</label>
          <select className="select input-md" value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">Alle bronnen</option>
            {sources.map((s) => (
              <option key={s.id} value={s.handle}>
                {s.handle}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Van</label>
          <input className="input input-md" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>Tot</label>
          <input className="input input-md" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div className="field">
          <label>Limiet</label>
          <input
            className="input input-sm"
            type="number"
            min={1}
            max={500}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          />
        </div>
        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Laden…' : q.trim() ? 'Zoeken' : 'Toon berichten'}
        </button>
        <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => void browseAll()}>
          Alles tonen
        </button>
      </form>

      {error && <div className="alert error">{error}</div>}

      {searched && (
        <div className="row muted" style={{ marginTop: 14 }}>
          <span>
            {browsing ? 'Bladeren · ' : 'Zoeken · '}
            {results.length} bericht{results.length === 1 ? '' : 'en'}
            {grouped.length ? ` in ${grouped.length} bron(nen)` : ''}
            {source ? ` · filter ${source}` : ''}
          </span>
          {results.length > 0 && (
            <a className="btn btn-ghost btn-sm" href={exportUrl}>
              Export CSV
            </a>
          )}
        </div>
      )}

      {!searched && (
        <div className="empty">
          Zoek op trefwoord, of klik <em>Alles tonen</em> om zonder zoekterm te bladeren.
          {sources[0] && (
            <>
              {' '}
              Per bron kan dat ook via <Link to="/sources">Bronnen</Link>.
            </>
          )}
        </div>
      )}
      {searched && results.length === 0 && <div className="empty">Geen berichten.</div>}

      {grouped.map(([handle, hits]) => (
        <section key={handle}>
          <h2 className="group-title">
            <Link to={`/search?source=${encodeURIComponent(handle)}&browse=1`}>{handle}</Link>
          </h2>
          <div className="msg-list">
            {hits.map((hit) => {
              const key = `${hit.source_id}-${hit.platform_message_id}`;
              const html = linkify(highlightText(hit.text || '[Media]', q));
              const ocrHtml = hit.ocr_text ? linkify(highlightText(hit.ocr_text, q)) : null;
              return (
                <article className="msg" key={key}>
                  <div className="msg-head">
                    <span>
                      {formatDateTime(hit.date_utc)}
                      {hit.match_in === 'ocr' && <span className="badge warn" style={{ marginLeft: 8 }}>in afbeelding</span>}
                      {hit.match_in === 'both' && <span className="badge ok" style={{ marginLeft: 8 }}>tekst + OCR</span>}
                    </span>
                    <div className="row">
                      {hit.permalink && (
                        <a href={hit.permalink} target="_blank" rel="noopener noreferrer">
                          Permalink
                        </a>
                      )}
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => void toggleSave(hit)}>
                        {saved.has(key) ? 'Opgeslagen' : 'Opslaan'}
                      </button>
                    </div>
                  </div>
                  {hit.media_path && (
                    <div style={{ marginBottom: 8 }}>
                      <a href={mediaUrl(hit.media_path)} target="_blank" rel="noopener noreferrer">
                        {isImageMedia(hit.media_path) ? (
                          <img
                            src={mediaUrl(hit.media_path)}
                            alt=""
                            className="msg-media-img"
                            loading="lazy"
                            style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8 }}
                          />
                        ) : (
                          <span className="btn btn-ghost btn-sm">Media openen</span>
                        )}
                      </a>
                    </div>
                  )}
                  <pre className="msg-text" dangerouslySetInnerHTML={{ __html: html }} />
                  {ocrHtml && (
                    <div style={{ marginTop: 8 }}>
                      <div className="muted" style={{ fontSize: '0.8rem', marginBottom: 4 }}>OCR uit afbeelding</div>
                      <pre className="msg-text" dangerouslySetInnerHTML={{ __html: ocrHtml }} />
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
